import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HeadSub from '../../../../components/comm/HeadSub';
import CustomDatePicker from '../../../../components/datepicker/CustomDatePicker';
import RecordAlarmSettingBox from '../../../../components/box/RecordAlarmSettingBox';
import style from './RecordSettingDetail.module.css';
import axiosApi from '../../../../api/daonAxios';
import CODE from '../../../../constants/code';
import { getPillEraTm } from '../../../../constants/pillEraTime';
import { formatDate, formatDateWithWeekday } from '../../../../assets/js/comm';
import Skeleton from '@mui/material/Skeleton';

const SheetOverlay = ({ open, onDismiss, children }) => {
    const [visible, setVisible] = useState(false);
    const [animating, setAnimating] = useState(false);

    useEffect(() => {
        if (open) {
            setVisible(true);
            requestAnimationFrame(() => {
                requestAnimationFrame(() => setAnimating(true));
            });
        } else {
            setAnimating(false);
            const timer = setTimeout(() => setVisible(false), 300);
            return () => clearTimeout(timer);
        }
    }, [open]);

    if (!visible) return null;

    return (
        <div className={style['sheet-overlay']} data-active={animating ? 'true' : 'false'}>
            <div className={style['sheet-backdrop']} onClick={onDismiss} />
            <div className={style['sheet-panel']}>
                {children}
            </div>
        </div>
    );
};

const ITEM_HEIGHT = 40;
const VISIBLE_COUNT = 5;

const DayPickerColumn = ({ options, value, onChange }) => {
    const containerRef = useRef(null);
    const isDragging = useRef(false);
    const startY = useRef(0);
    const startScrollTop = useRef(0);

    const selectedIndex = options.indexOf(value);

    useEffect(() => {
        if (containerRef.current && !isDragging.current) {
            requestAnimationFrame(() => {
                if (containerRef.current) {
                    containerRef.current.scrollTop = selectedIndex * ITEM_HEIGHT;
                }
            });
        }
    }, [selectedIndex, options]);

    const handleScroll = () => {
        if (!containerRef.current) return;
        const scrollTop = containerRef.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
        if (options[clampedIndex] !== value) {
            onChange(options[clampedIndex]);
        }
    };

    const snapToItem = () => {
        if (!containerRef.current) return;
        const scrollTop = containerRef.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        const clampedIndex = Math.max(0, Math.min(index, options.length - 1));
        containerRef.current.scrollTo({
            top: clampedIndex * ITEM_HEIGHT,
            behavior: "smooth"
        });
    };

    const handleTouchStart = (e) => {
        isDragging.current = true;
        startY.current = e.touches[0].clientY;
        startScrollTop.current = containerRef.current?.scrollTop || 0;
    };

    const handleTouchMove = (e) => {
        if (!isDragging.current || !containerRef.current) return;
        const deltaY = startY.current - e.touches[0].clientY;
        containerRef.current.scrollTop = startScrollTop.current + deltaY;
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
        snapToItem();
    };

    return (
        <div
            ref={containerRef}
            onScroll={handleScroll}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseUp={snapToItem}
            style={{
                height: ITEM_HEIGHT * VISIBLE_COUNT,
                overflow: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                flex: 1,
                position: "relative"
            }}
        >
            <div style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }} />

            {options.map((option) => {
                const isSelected = option === value;
                return (
                    <div
                        key={option}
                        onClick={() => {
                            onChange(option);
                            const idx = options.indexOf(option);
                            containerRef.current?.scrollTo({
                                top: idx * ITEM_HEIGHT,
                                behavior: "smooth"
                            });
                        }}
                        style={{
                            height: ITEM_HEIGHT,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: isSelected ? "24px" : "18px",
                            fontWeight: "700",
                            color: isSelected ? "#10141A" : "#D2D6DC",
                            transition: "all 0.2s",
                            cursor: "pointer"
                        }}
                    >
                        {option}
                    </div>
                );
            })}

            <div style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }} />
        </div>
    );
};

const RecordSettingDetail = () => {
    /*****************
     * 주요 변수 선언부
     ******************/
    const user = JSON.parse(sessionStorage.getItem("loginUser"));

    const [editingMedicineIndex, setEditingMedicineIndex] = useState(null);
    const [pickerDays, setPickerDays] = useState({ day: 5 });

    const navigate = useNavigate();
    const location = useLocation();

    const [selectedDate, setSelectedDate] = useState("");
    const [activePicker, setActivePicker] = useState(null);

    const [medicineInfoList, setMedicineInfoList] = useState([]);
    const [selectedMedicineInfo, setSelectedMedicineInfo] = useState([]);

    // 복약 기간 동적으로 날짜 생성 - 편집 중인 약 기준
    const dayOptions = useMemo(() => {
        if (editingMedicineIndex !== null && selectedMedicineInfo[editingMedicineIndex]) {
            const grpCd = selectedMedicineInfo[editingMedicineIndex].tkmdcnGrpCd;
            const original = medicineInfoList.find(m => m.tkmdcnGrpCd === grpCd);
            return Array.from({ length: original?.totDays || 60 }, (v, i) => i + 1);
        }
        return Array.from({ length: medicineInfoList[0]?.totDays || 60 }, (v, i) => i + 1);
    }, [editingMedicineIndex, selectedMedicineInfo, medicineInfoList]);

    // bottom sheet (오버레이)
    const [startDayopen, setStartDayOpen] = useState(false);
    const [daySelectOpen, setDaySelectOpen] = useState(false);
    const [recordAlarmOpen, setRecordAlarmOpen] = useState(false);
    const [alarmTimeOpen, setAlarmTimeOpen] = useState(false);

    // 전송중 체크
    const isSendingRef = useRef(false);
    const isInitialRender = useRef(true);
    const [isLoading, setIsLoading] = useState(false);

    // push에서 올 때도 location.state로 통일
    const item = location.state?.item || null;
    const paramData = location.state?.paramData || null;

    const [baseData, setBaseData] = useState({ ...item });

    /*****************
     * 기능 함수 선언부
     ******************/

    // 알람 시간 및 재알람 여부 설정
    const handleConfirmTime = (itemIdx, groupIdx, value) => {
        const hour = value.hour.toString().padStart(2, '0');
        const minute = value.minute.toString().padStart(2, '0');
        const convertedTime = `${hour}${minute}00`;
        setSelectedMedicineInfo(prevList =>
            prevList.map((item, currentItemIdx) => {
                if (currentItemIdx !== itemIdx) return item;
                return {
                    ...item,
                    groups: item.groups.map((group, currentGroupIdx) => {
                        if (currentGroupIdx !== groupIdx) return group;
                        return {
                            ...group,
                            selectedTime: convertedTime,
                            reAlrmAt: value.reAlrmAt,
                        };
                    }),
                };
            })
        );
        setActivePicker(false);
    };

    // 알람 여부 설정
    const handleSwitchChange = (itemIdx, groupIdx, checked) => {
        if (checked) {
            setActivePicker({ itemIdx, groupIdx });
            setSelectedMedicineInfo(prevList =>
                prevList.map((item, i) =>
                    i !== itemIdx ? item : {
                        ...item,
                        groups: item.groups.map((group, j) =>
                            j !== groupIdx ? group : { ...group, alrmAt: '1' }
                        ),
                    }
                )
            );
        } else {
            setSelectedMedicineInfo(prevList =>
                prevList.map((item, i) =>
                    i !== itemIdx ? item : {
                        ...item,
                        groups: item.groups.map((group, j) =>
                            j !== groupIdx ? group : { ...group, selectedTime: null, alrmAt: '0' }
                        ),
                    }
                )
            );
        }
    };

    // 스위치가 켜져 있는데 시간 설정 없이 닫으면 OFF 처리
    const onClose = (itemIdx, groupIdx) => {
        setSelectedMedicineInfo(prevList =>
            prevList.map((item, currentIndex) => {
                if (currentIndex !== itemIdx) return item;
                return {
                    ...item,
                    groups: item.groups.map((group, currentGroupIdx) => {
                        if (currentGroupIdx !== groupIdx) return group;
                        return { ...group, alrmAt: '0' };
                    })
                };
            })
        );
        setActivePicker(null);
    };

    const handleDateConfirm = (date) => {
        if (!date) return;
        const picked = new Date(date);
        if (isNaN(picked)) return;

        const y = picked.getFullYear();
        const m = String(picked.getMonth() + 1).padStart(2, '0');
        const d = String(picked.getDate()).padStart(2, '0');

        setSelectedDate(`${y}.${m}.${d}`);
        // onDismiss가 모두 닫은 후에 열기
        setTimeout(() => {
            setRecordAlarmOpen(true);
        }, 50);
    };

    const getAlarmPeriod = (startDe, endDe) => {
        const formattedStartDe = startDe.substring(2);
        const [startY, startM, startD] = startDe.split('.').map(Number);
        const startDateObj = new Date(startY, startM - 1, startD);

        const endY = parseInt(endDe.substring(0, 4), 10);
        const endM = parseInt(endDe.substring(4, 6), 10);
        const endD = parseInt(endDe.substring(6, 8), 10);
        const formattedEndDe = `${String(endY).slice(-2)}.${String(endM).padStart(2, '0')}.${String(endD).padStart(2, '0')}`;
        const endDateObj = new Date(endY, endM - 1, endD);

        const msPerDay = 1000 * 60 * 60 * 24;
        const diffInMs = endDateObj.getTime() - startDateObj.getTime();
        const totDays = Math.round(diffInMs / msPerDay) + 1;

        return `${formattedStartDe} ~ ${formattedEndDe} (총 ${totDays}일)`;
    };

    const getPeriod = (startDe, totDays) => {
        const [year, month, day] = startDe.split('.').map(num => parseInt(num, 10));
        const dateObj = new Date(year, month - 1, day);
        dateObj.setDate(dateObj.getDate() + (totDays - 1));

        const endYear = dateObj.getFullYear();
        const endMonth = String(dateObj.getMonth() + 1).padStart(2, '0');
        const endDay = String(dateObj.getDate()).padStart(2, '0');

        return `${startDe} ~ ${endYear}.${endMonth}.${endDay}`;
    };

    const getDayAttr = (grpNm) => {
        if (grpNm === '아침') return '1';
        if (grpNm === '점심') return '2';
        if (grpNm === '저녁') return '3';
        if (grpNm === '취침전') return '4';
        return '5';
    };

    // 초기화
    useEffect(() => {
        setStartDayOpen(false);
        setDaySelectOpen(false);
        setRecordAlarmOpen(false);
        setAlarmTimeOpen(false);
    }, []);

    useEffect(() => {
        if (isInitialRender.current) {
            searchMedicineInfoList();
            if (item?.hsptlNm === undefined || item?.deptNm === undefined) {
                searchMedicineBaseInfo();
            }
            isInitialRender.current = false;
        }
    }, [item?.hsptlNm, item?.deptNm]);

    const buildUserTatkingMedicineRequest = () => {
        return {
            usrInfoId: user.usrInfoId,
            hsptlCd: baseData.hsptlCd,
            hsptlNm: baseData.hsptlNm,
            patntId: baseData.patntId,
            deptCd: baseData.deptCd,
            deptNm: baseData.deptNm,
            prscrptnDt: baseData.prscrptnDt,
            usrTkmdcnDetailList: selectedMedicineInfo.flatMap((med) => {
                return med.groups.map((grp) => ({
                    usrInfoId: user.usrInfoId,
                    tkmdcnGrpCd: med.tkmdcnGrpCd,
                    tkmdcnEraCd: getDayAttr(grp.name),
                    tkmdcnMthCn: med?.tkmdcnDivNm,
                    tkmdcnPdCn: calculatePeriodString(selectedDate, med.totDays),
                    tkmdcnCn: med.tkmdcnTxt,
                    tkmdcnCdCn: med.tkmdcnCds,
                    totTkmdcnCo: med.tkmdcnCnt,
                    totTkmdcnDaycnt: med.totDays,
                    cnncHsptlCd: baseData.hsptlCd,
                    cnncPatntNo: baseData.patntId,
                    cnncPrscDe: baseData.prscrptnDt,
                    cnncDeptCd: baseData.deptCd,
                    cnncHsptlNm: baseData.hsptlNm,
                    cnncDeptNm: baseData.deptNm,
                    alrmSetupDe: selectedDate.replace(/\./g, ''),
                    alrmSetupTm: grp.selectedTime || getPillEraTm(getDayAttr(grp.name)),
                    alrmAt: grp.alrmAt,
                    reAlrmAt: grp.reAlrmAt,
                }));
            }),
        }
    };

    const calculatePeriodString = (startDe, totDays) => {
        const [year, month, day] = startDe.split('.').map(Number);
        const startDate = new Date(year, month - 1, day);
        const endDate = new Date(year, month - 1, day);
        endDate.setDate(endDate.getDate() + (totDays - 1));

        const fmt = (d) =>
            `${String(d.getFullYear()).slice(2)}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

        return `${fmt(startDate)} ~ ${fmt(endDate)} (총 ${totDays}일)`;
    };

    const searchMedicineBaseInfo = () => {
        setIsLoading(true);
        const requestRecordListUrl = "/homecare/tkmdcn/retrieveTakingMedicineInfo";
        const jToken = localStorage.getItem('jToken');
        const requestParams = item || paramData;
        const requestOptions = {
            headers: { Authorization: `Bearer ${jToken}` }
        };

        axiosApi.post(requestRecordListUrl, requestParams, requestOptions)
            .then((res) => {
                const resultData = res.data;
                if (resultData.resultCode === CODE.RCV_SUCCESS) {
                    setBaseData({ ...resultData.result?.tkmdcnInfo });
                } else {
                    setBaseData({ ...requestParams });
                }
            })
            .catch(err => {
                console.log(" ===========> searchMedicineBaseInfo error : ", err);
            });
    };

    const searchMedicineInfoList = async () => {
        setIsLoading(true);
        const requestRecordListUrl = "/homecare/tkmdcn/retrieveTakingMedicineDetailList";
        const jToken = localStorage.getItem('jToken');
        const requestOptions = {
            headers: { Authorization: `Bearer ${jToken}` }
        };
        const requestData = paramData;

        axiosApi.post(requestRecordListUrl, { ...requestData, usrInfoId: user.usrInfoId }, requestOptions)
            .then((res) => {
                const resData = res.data;
                if (resData.resultCode === CODE.RCV_SUCCESS) {
                    console.log("tkmdcnDetailList raw:", JSON.stringify(resData.result.tkmdcnDetailList[0]));
                    const processedList = resData.result.tkmdcnDetailList.map(item => {
                        const groups = (item.tkmdcnGrpNm ?? '')
                            .split(',')
                            .map(s => s.trim())
                            .filter(Boolean)
                            .map(name => ({
                                name,
                                alrmAt: '0',
                                selectedTime: null,
                                reAlrmAt: '0',
                            }));
                        return { ...item, isAlarmed: false, groups };
                    });
                    setMedicineInfoList(processedList);
                } else {
                    setMedicineInfoList([]);
                }
            })
            .catch(err => {
                console.log(" ==================> retrieveTakingMedicineDetailList error : ", err);
            })
            .finally(() => {
                setIsLoading(false);
            })
    };

    const saveUserTakingMedicineDetailList = async () => {
        if (isSendingRef.current) return;
        isSendingRef.current = true;
        setIsLoading(true);
        const requestRecordListUrl = "/homecare/tkmdcn/saveUserTakingMedicineDetailList";
        const jToken = localStorage.getItem('jToken');
        const requestOptions = {
            headers: { Authorization: `Bearer ${jToken}` }
        };
        const requestData = buildUserTatkingMedicineRequest();

        try {
            const res = await axiosApi.post(requestRecordListUrl, requestData, requestOptions);
            const resData = res.data;
            if (resData.resultCode === CODE.RCV_SUCCESS) {
                setMedicineInfoList(prevList => {
                    const selectedTxts = new Set(selectedMedicineInfo.map(med => med.tkmdcnTxt));
                    return prevList.map(item => {
                        if (selectedTxts.has(item.tkmdcnTxt)) {
                            return { ...item, isAlarmed: true };
                        }
                        return item;
                    });
                });
                onDismiss();
                searchMedicineInfoList();
            } else {
                alert(resData.resultMessage);
            }
        } catch (err) {
            console.log(" ==================> saveUserTakingMedicineDetailList error : ", err);
        } finally {
            isSendingRef.current = false;
            setIsLoading(false);
        }
    };

    // 모두 닫기
    function onDismiss() {
        try {
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
        } catch (e) { /* ignore */ }
        setStartDayOpen(false);
        setDaySelectOpen(false);
        setRecordAlarmOpen(false);
        setAlarmTimeOpen(false);
    }

    // 선택완료 후 → 알림 시간 설정 sheet 열기
    function nextOpen() {
        setStartDayOpen(false);
        setRecordAlarmOpen(true);
    }

    // 복약 기간 선택 취소 → 시작일 sheet로 복귀
    function preDismiss() {
        setEditingMedicineIndex(null);
        setDaySelectOpen(false);
    }

    // 복약 기간 선택완료 → 값 반영 후 시작일 sheet로 복귀
    function applyDayChange() {
        if (editingMedicineIndex !== null) {
            setSelectedMedicineInfo(prev =>
                prev.map((item, idx) =>
                    idx === editingMedicineIndex
                        ? { ...item, totDays: pickerDays.day }
                        : item
                )
            );
        }
        setEditingMedicineIndex(null);
        setDaySelectOpen(false);
    }

    return (
        <>
            <HeadSub title="기록 설정 상세" />

            <div id="sub-content">
                <div className='inner-wrap'>

                    <div className={style['title-box']}>
                        <div className={style['group-box']}>
                            <span>{baseData?.hsptlNm}</span>
                            <span>{baseData?.deptNm} 처방</span>
                        </div>
                        <p>{formatDate(baseData?.prscrptnDt)}</p>
                    </div>

                    <div className={style['drug-lists-box']}>
                        <strong>복약 기록</strong>

                        <div className={style['check-point-box']}>
                            <ul>
                                <li>알림이 적용되기까지 최대 30분 정도 걸릴 수 있습니다.</li>
                            </ul>
                        </div>

                        <ul>
                            {isLoading ? (
                                <>
                                    {[1, 2].map((skelItem) => (
                                        <li key={skelItem}>
                                            <div className={style['top-box']}>
                                                <div className={style['day-box']}>
                                                    <Skeleton variant="text" width={50} height={24} style={{ display: 'inline-block', borderRadius: '8px' }} />
                                                    <Skeleton variant="text" width={50} height={24} style={{ display: 'inline-block', borderRadius: '8px' }} />
                                                </div>
                                                <Skeleton variant="text" width={70} height={30} style={{ backgroundColor: 'var(--color-main-5)' }} />
                                            </div>
                                            <div className={style['bot-box']}>
                                                <ul>
                                                    <li>
                                                        <div className={style['take-box']}>
                                                            <Skeleton variant="text" width="60%" />
                                                        </div>
                                                        <div className={style['pill-name-box']}>
                                                            <span>
                                                                <Skeleton variant="text" width={100} />
                                                                <Skeleton variant="text" width={60} />
                                                                <Skeleton variant="text" width={80} />
                                                            </span>
                                                        </div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </li>
                                    ))}
                                </>
                            ) : medicineInfoList.map((item, index) => (
                                <li key={item.tkmdcnGrpCd || index}>
                                    <div className={style['top-box']}>
                                        <div className={style['day-box']}>
                                            {item.groups.map((grp, gIdx) => <span key={gIdx} data-day={getDayAttr(grp.name)}>{grp.name}</span>)}
                                            {item.tkmdcnDivNm && <span>{item.tkmdcnDivNm}</span>}
                                        </div>

                                        {item.alrmSetupDe ? (
                                            <div className={style['date-box']}>
                                                <p>{formatDate(item.alrmSetupDe)}부터</p>
                                                <p>{item.alrmSetupDays}일 복용</p>
                                            </div>
                                        ) : (
                                            <button className='btn' data-btn-type="fill-main" data-btn-size='sm' onClick={() => { setStartDayOpen(true); setSelectedMedicineInfo([item]); }}>
                                                <span>복약 시작</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className={style['bot-box']}>
                                        <ul>
                                            <li>
                                                <div className={style['take-box']}>
                                                    <p>{item.totDays}일</p>
                                                    <p>{item.tkmdcnCnt}가지 약</p>
                                                </div>
                                                <div className={style['pill-name-box']}>
                                                    <span>{item.tkmdcnTxt.replace(/@,@/g, ', ')}</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {medicineInfoList?.length > 0 && medicineInfoList.some(item => !item.alrmSetupDe) &&
                        <div className={style['btn-box']}>
                            <button className='btn' data-btn-type="fill-main" data-btn-size='md' data-btn-width='full' onClick={() => {
                                const notStartedMedicines = medicineInfoList.filter(item => !item.alrmSetupDe);
                                setStartDayOpen(true);
                                setSelectedMedicineInfo(notStartedMedicines);
                            }}>
                                <span>일괄 복약 시작</span>
                            </button>
                        </div>
                    }

                    <div className={style['noti-list-box']}>
                        <ul>
                            <li>대체약으로 조제된 경우 병원 처방 받은 약 목록과 약국에서 수령한 약 목록은 서로 다를 수 있습니다.</li>
                            <li>위 정보는 의사, 약사의 복약 지시와 일치하지 않을 수 있습니다. 반드시 의사, 약사의 복약 지시에 따라 복용하세요.</li>
                        </ul>
                    </div>

                    {/*==== 복약 시작일 선택::S ====*/}
                    <SheetOverlay open={startDayopen} onDismiss={onDismiss}>
                        <CustomDatePicker
                            onClose={onDismiss}
                            type="record"
                            titleTxt="복약 시작일을 선택해주세요"
                            subTitleTxt="선택한 날 부터 복약 기록이 시작됩니다."
                            nextOpen={null}
                            onConfirm={handleDateConfirm}
                        >

                            <div className={style['picker-top-box']}>
                                <div className={style['record-title-box']}>
                                    <p>{baseData?.hsptlNm}</p>
                                    <p>{formatDateWithWeekday(baseData?.prscrptnDt)} 처방</p>
                                </div>
                                <div className={style['record-hisyory-list']}>
                                    <ul>
                                        {selectedMedicineInfo.map((item, index) => (
                                            <li key={item.tkmdcnGrpCd || index}>
                                                <div className={style['left-box']}>
                                                    {item.groups.map((grp, gIdx) => <span key={gIdx} data-day={getDayAttr(grp.name)}>{grp.name}</span>)}
                                                    {item.tkmdcnDivNm && <span>{item.tkmdcnDivNm}</span>}
                                                </div>
                                                <div className={style['right-box']}>
                                                    <span onClick={() => {
                                                        setEditingMedicineIndex(index);
                                                        setPickerDays({ day: item.totDays });
                                                        setDaySelectOpen(true);
                                                    }}>
                                                        {item.totDays}일
                                                    </span>
                                                    <p>동안 복약</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </CustomDatePicker>
                    </SheetOverlay>
                    {/*==== 복약 시작일 선택::E ====*/}

                    {/*==== 알림 시간 설정::S ===*/}
                    <SheetOverlay open={recordAlarmOpen && !alarmTimeOpen} onDismiss={onDismiss}>
                        <div className={style['record-alarm-setting-box']}>
                            <div className={style['record-alarm-top-box']}>
                                <p>{baseData?.hsptlNm}</p>
                                <p>{formatDateWithWeekday(baseData?.prscrptnDt)} 처방</p>
                            </div>

                            <p className={style['record-alarm-tit']}>알림 시간을 설정해주세요</p>
                            <span className={style['record-alarm-subtit']}>복약 기간동안 설정한 시간에 복약 알림이 울립니다.</span>

                            {selectedMedicineInfo.map((item, index) => (
                                <React.Fragment key={item.tkmdcnTxt}>
                                    {item.groups.map((grp, idx) =>
                                        <RecordAlarmSettingBox
                                            key={`${item.tkmdcnGrpCd}-${grp.name}-${index}-${idx}`}
                                            isOn={grp.alrmAt === '1'}
                                            dayType={getDayAttr(grp.name)}
                                            dayLabel={grp.name}
                                            subtitle={item.tkmdcnDivNm ?? null}
                                            time={grp.selectedTime}
                                            period={getPeriod(selectedDate, item.totDays)}
                                            totDays={item.totDays}
                                            handleConfirmTime={(value) => handleConfirmTime(index, idx, value)}
                                            handleSwitchChange={(checked) => handleSwitchChange(index, idx, checked)}
                                            onDismiss={() => onClose(index, idx)}
                                            alarmTimeOpen={activePicker?.itemIdx === index && activePicker?.groupIdx === idx}
                                        />
                                    )}
                                </React.Fragment>
                            ))}

                            <div className="bottom-fixed-box">
                                <div className="inner-box">
                                    <div className='btn-layout' data-btn-num="1">
                                        <button className="btn" data-btn-type="fill-main" data-btn-size="lg" onClick={() => saveUserTakingMedicineDetailList()}>
                                            <span>알림설정 완료</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SheetOverlay>
                    {/*==== 알림 시간 설정::E ===*/}

                    {/*=== 복약 기간 선택::S ====*/}
                    <SheetOverlay open={daySelectOpen} onDismiss={onDismiss}>
                        <div className="day-picker-wrap">
                            <div className={style['current-day']}>
                                <span>{pickerDays.day}일</span>
                            </div>

                            <strong className="picker-title">복약 기간을 선택해주세요</strong>
                            <span>선택한 기간 동안 복약 기록이 진행됩니다.</span>

                            <div className="day-picker-box">
                                <div className="picker-sub-txt">
                                    <span>일</span>
                                </div>

                                <div style={{ display: "flex", width: "70%", position: "relative", margin: "0 auto" }}>
                                    {/* 선택 영역 하이라이트 */}
                                    <div style={{
                                        position: "absolute",
                                        top: "50%",
                                        left: 0,
                                        right: 0,
                                        height: 40,
                                        transform: "translateY(-50%)",
                                        background: "#F2F4F8",
                                        borderRadius: "8px",
                                        pointerEvents: "none",
                                        zIndex: 0
                                    }} />
                                    <DayPickerColumn
                                        options={dayOptions}
                                        value={pickerDays.day}
                                        onChange={(v) => setPickerDays({ day: v })}
                                    />
                                </div>
                            </div>

                            <div className="bottom-box" style={{ width: "100%" }}>
                                <div className="inner-box">
                                    <div className='btn-layout' data-btn-num="2">
                                        <button className="btn" data-btn-type="outline-main" data-btn-size="lg" onClick={preDismiss}>
                                            <span>취소</span>
                                        </button>
                                        <button className="btn" data-btn-type="fill-main" data-btn-size="lg" onClick={applyDayChange}>
                                            <span>선택완료</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </SheetOverlay>
                    {/*=== 복약 기간 선택::E ====*/}
                </div>
            </div>
        </>
    );
};

export default RecordSettingDetail;